import { deserialize } from '@plone/volto-slate/editor/deserialize';
import { WordDetector } from '../../../../utils/wordContentProcessor/WordDetector';
import { WordCleaner } from '../../../../utils/wordContentProcessor/WordCleaner';
import { WordTableParser } from '../../../../utils/wordContentProcessor/WordTableParser';
import { BulletListConverter } from '../../../../utils/wordContentProcessor/HtmlListConverter';
import {
  SlateListProcessor,
  SlateNodeCleaner,
} from '../../../..//utils/content/ListProcessor';

/**
 * Handles HTML content data transfer operations
 * Processes text/html MIME type with Word content detection and cleaning
 */
export class HtmlContentHandler {
  constructor(editor) {
    this.editor = editor;
    this.wordCleaner = new WordCleaner();
    this.wordTableParser = new WordTableParser();
    this.bulletConverter = new BulletListConverter();
    this.slateListProcessor = new SlateListProcessor();
    this.slateNodeCleaner = new SlateNodeCleaner();
  }

  async handle(data) {
    let processedHtml = data;

    // 1. Detect Word table content and check if we're in a table block cell
    const isWordDetected = WordDetector.isWordContent(data);
    const hasWordTable = WordTableParser.hasWordTable(data);
    const isInTableBlock = this.isInTableBlockCell();

    // If Word table is pasted into a table block cell, replace the entire table
    if (isWordDetected && hasWordTable && isInTableBlock) {
      return this.handleWordTableInTableBlock(data);
    }

    // Otherwise, proceed with normal processing
    if (isWordDetected) {
      processedHtml = this.wordCleaner.clean(data);
    }

    // 2. Process hyphen lists for all HTML content (not just Word)
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = processedHtml;
    this.bulletConverter.convertHyphenListsToHtml(tempDiv);
    processedHtml = tempDiv.innerHTML;

    const parsed = new DOMParser().parseFromString(processedHtml, 'text/html');

    const body =
      parsed.getElementsByTagName('google-sheets-html-origin').length > 0
        ? parsed.querySelector('google-sheets-html-origin > table')
        : parsed.body;

    const val = deserialize(this.editor, body);
    let fragment = Array.isArray(val) ? val : [val];

    fragment = this.editor.normalizeExternalData(fragment);

    // Apply numbered list conversion to Slate fragments
    fragment = this.slateListProcessor.convertNumberedLists(fragment);

    // Apply Slate-level filtering to remove empty paragraphs and text nodes
    fragment = this.slateNodeCleaner.filterEmptyNodes(fragment);

    this.editor.insertFragment(fragment);

    return true;
  }

  /**
   * Checks if the current editor is within a table block cell
   * @returns {boolean}
   */
  isInTableBlockCell() {
    // Check if the editor has getBlockProps method (indicates it's in a block context)
    if (!this.editor.getBlockProps) {
      return false;
    }

    try {
      const blockProps = this.editor.getBlockProps();

      // Check if the parent block is a table block
      return blockProps && blockProps.data && blockProps.data.table;
    } catch (error) {
      return false;
    }
  }

  /**
   * Handles Word table content pasted into a table block cell
   * Replaces the entire table block with the Word table data
   * @param {string} data - Word table HTML content
   * @returns {boolean}
   */
  async handleWordTableInTableBlock(data) {
    try {
      // Get the table block context first to access original table data
      const blockProps = this.editor.getBlockProps();
      if (!blockProps || !blockProps.onChangeBlock) {
        return false;
      }

      // Parse the Word table, preserving original table settings
      const originalTableData = blockProps.data?.table;

      const tableData = this.wordTableParser.parseWordTable(
        data,
        originalTableData,
      );

      if (!tableData) {
        return false;
      }

      // Replace the entire table block data
      blockProps.onChangeBlock(blockProps.block, {
        ...blockProps.data,
        table: tableData,
      });

      return true;
    } catch (error) {
      return false;
    }
  }
}
